import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

// Images
import FallbackBanner from '../../assets/banner-1.svg';
import { ratingToPercent } from '../../utils/rating';
import FallbackCategory from '../../assets/nearby-merchants/1.png';
import SpinnerIcon from '../../assets/spinner3.svg';

import { useUser } from '../../contexts/user';
import { useAppliedFilters } from '../../hooks/useAppliedFilters';
import { DRIVE_SPEED_KMH } from '../../config/appConfig';
import {
  partitionFilters,
  merchantMatchesCategorySlugs,
} from '../../utils/filters';

// NOTE: filter-config registration (mode: 'merchants' + available categories)
// lives at the page level in pages/home/index.js — NOT here. This component
// is lazy-mounted via useInView, so registering filter config here meant the
// FilterModal showed an empty category list when opened before the user
// scrolled to "Nearby Merchants".

const NearbyMerchants = ({ merchants = [], loading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [hoveredId, setHoveredId] = useState(null);
  const appliedFilters = useAppliedFilters();

  // Haversine formula to calculate distance in km
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (
      typeof lat1 !== 'number' || typeof lon1 !== 'number' ||
      typeof lat2 !== 'number' || typeof lon2 !== 'number'
    ) {
      return null;
    }
    const R = 6371; // radius of Earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return +(R * c).toFixed(2);
  };

  const getTravelTimeByCar = (distanceKm) => {
    if (typeof distanceKm !== 'number' || distanceKm <= 0) {
      return null;
    }
    const timeHours = distanceKm / DRIVE_SPEED_KMH;
    const timeMinutes = Math.round(timeHours * 60);
    return timeMinutes;
  };

  const { sortBy, categorySlugs } = partitionFilters(appliedFilters);

  const filteredMerchants = merchants.filter((m) => {
    return merchantMatchesCategorySlugs(m, categorySlugs);
  });

  if (loading) {
    return <div className="flex items-center justify-center py-10">
      <img
        src={SpinnerIcon}
        alt='Loading...'
        className="h-5 w-5 animate-spin mx-auto"
      />
    </div>;
  }

  return (
    <section className="pt-6 pb-20 lg:pb-40 px-2 md:px-4 lg:px-6">
      <h2 className="mb-3 text-xl font-semibold text-[#000000DE]">{t('Popular Brands')}</h2>
      <div className="
        grid grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-3
        2xl:grid-cols-4
        gap-6
      ">
        {[...filteredMerchants].sort((a, b) => {
          if (sortBy === 'minOrderAmount') {
            const amtA = Number(a?.marketplaceMinOrderValue ?? Infinity);
            const amtB = Number(b?.marketplaceMinOrderValue ?? Infinity);
            return amtA - amtB;
          }
          if (sortBy === 'avgRating') {
            const rA = Number(a?.rating?.averageRating ?? 0);
            const rB = Number(b?.rating?.averageRating ?? 0);
            return rB - rA;
          }
          const distA = user?.locations?.[0]
            ? getDistanceKm(
              user.locations[0].latitude,
              user.locations[0].longitude,
              a.address?.latitude,
              a.address?.longitude,
            )
            : Infinity;
          const distB = user?.locations?.[0]
            ? getDistanceKm(
              user.locations[0].latitude,
              user.locations[0].longitude,
              b.address?.latitude,
              b.address?.longitude,
            )
            : Infinity;
          return distA - distB;
        }).map((merchant = {}, index) => {
          const {
            _id: merchantConfigId,
            coverImage,
            rating = { averageRating: null, count: 0 },
            sponsored,
            logoImage,
            storeName,
            address = { latitude: null, longitude: null },
            categories = { enNames: [], frNames: [] },
          } = merchant;

          const distanceKm = user?.locations?.[0]
            ? getDistanceKm(
              user.locations[0].latitude,
              user.locations[0].longitude,
              address.latitude,
              address.longitude,
            )
            : null;

          const travelTimeMins = distanceKm !== null
            ? getTravelTimeByCar(distanceKm)
            : null;

          const ratingPercent = rating && typeof rating === 'object' && rating.count > 0
            ? ratingToPercent(rating.averageRating)
            : null;

          return (
            <div
              key={merchantConfigId || index}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/merchant-details/${merchantConfigId}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/merchant-details/${merchantConfigId}`); } }}
              onMouseEnter={() => setHoveredId(merchantConfigId)}
              onMouseLeave={() => setHoveredId(null)}
              className="nearby-merchants-card rounded-lg overflow-hidden cursor-pointer"
            >
              {/* Banner */}
              <div
                className="h-[150px] sm:h-[180px] md:h-[200px] w-full relative bg-cover bg-center"
                style={{
                  backgroundImage: `url(${coverImage || FallbackBanner})`,
                }}
              >
                {hoveredId === merchantConfigId && (
                  <div className="absolute inset-0 bg-black opacity-5 z-10 transition-opacity duration-300"></div>
                )}
                {/* Rating */}
                {ratingPercent && (
                  <div className="text-xs leading-4 flex items-center justify-center gap-1 py-[6px] px-3 bg-black text-white rounded-[100px] absolute right-2 bottom-2">
                    {ratingPercent}
                  </div>
                )}

                {/* Sponsored */}
                {sponsored && (
                  <span className="bg-[#000000B3] py-1 px-2 text-xs text-white leading-4 rounded-tr text-center absolute left-0 bottom-0">
                    {t('Sponsored')}
                  </span>
                )}
              </div>

              {/* Details */}
              <div className="bg-white py-4 px-3 md:px-4 flex items-center gap-3 flex-1 relative">
                {hoveredId === merchantConfigId && (
                  <div className="absolute inset-0 bg-black opacity-5 z-10 transition-opacity duration-300"></div>
                )}
                {/* Category image */}
                <div className="w-[62px] h-[62px] rounded-lg overflow-hidden">
                  <img
                    src={logoImage || FallbackCategory}
                    alt={storeName || 'merchant'}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex flex-col gap-1.5 flex-1">
                  <h3 className="leading-[1.2] capitalize truncate font-semibold mb-[1px] text-[15.2px] text-balance line-clamp-1 ">
                    {storeName || 'Unnamed Store'}
                  </h3>

                  <ul className="text-xs flex items-center font-medium text-[#6F6F6F]">
                    {travelTimeMins !== null && (
                      <li>{travelTimeMins} {t('mins')}</li>
                    )}
                    {distanceKm !== null && (
                      <li className="flex items-center">
                        <div className="w-1 h-1 bg-[#6F6F6F] rounded-full mx-2"></div>
                        {distanceKm} {t('kilometers')}
                      </li>
                    )}
                  </ul>

                  <p className="font-medium text-xs leading-4 truncate text-[#8F8F8F]">
                    {Array.isArray(categories?.enNames) && categories.enNames.length > 0
                      ? categories.enNames.join(', ')
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default NearbyMerchants;
